import { createTheme } from "@mui/material/styles";
import {
	goniniBlue,
	goniniGreen,
	goniniOrange,
	goniniFontFamily,
	goniniRed,
} from "./gonini";

const white = "rgba(255,255,255,1)";

const typography = {
	fontFamily: goniniFontFamily,
	h1: { fontWeight: 700, letterSpacing: "-0.02em" },
	h2: { fontWeight: 700, letterSpacing: "-0.02em" },
	h3: { fontWeight: 600, letterSpacing: "-0.015em" },
	h4: { fontWeight: 600, letterSpacing: "-0.01em" },
	h5: { fontWeight: 600, letterSpacing: "-0.01em" },
	h6: { fontWeight: 600 },
	button: { fontWeight: 600, textTransform: "none", letterSpacing: 0 },
};

const shape = { borderRadius: 8 };

const sharedComponents = {
	MuiButton: {
		defaultProps: {
			disableElevation: true,
		},
	},
	MuiAppBar: {
		defaultProps: {
			elevation: 0,
		},
		styleOverrides: {
			root: {
				borderBottom: `1px solid ${goniniBlue[800]}`,
			},
		},
	},
	MuiPaper: {
		styleOverrides: {
			root: {
				backgroundImage: "none",
			},
		},
	},
	MuiTooltip: {
		styleOverrides: {
			tooltip: {
				backgroundColor: goniniBlue[900],
				fontSize: "0.8125rem",
			},
			arrow: {
				color: goniniBlue[900],
			},
		},
	},
};

export const LightTheme = createTheme({
	palette: {
		mode: "light",
		primary: {
			main: goniniBlue[500],
			light: goniniBlue[400],
			dark: goniniBlue[700],
			contrastText: white,
		},
		secondary: {
			main: goniniBlue[700],
			light: goniniBlue[500],
			dark: goniniBlue[900],
			contrastText: white,
		},
		tertiary: {
			main: goniniBlue[900],
		},
		destructive: {
			main: goniniRed,
			contrastText: white,
		},
		success: {
			main: goniniGreen[700],
			light: goniniGreen[500],
			dark: goniniGreen[800],
			contrastText: white,
		},
		warning: {
			main: goniniOrange[700],
			light: goniniOrange[500],
			dark: goniniOrange[800],
			contrastText: white,
		},
		info: {
			main: goniniBlue[400],
			contrastText: white,
		},
		text: {
			primary: goniniBlue[900],
			secondary: goniniBlue[600],
		},
		divider: goniniBlue[50],
		appbar: {
			main: goniniBlue[900],
			contrastText: white,
		},
		statusbox: {
			main: white,
		},
		background: {
			default: "#f4f5f8",
			paper: white,
		},
		alert: {
			main: white,
		},
	},
	typography,
	shape,
	components: {
		...sharedComponents,
		MuiButton: {
			...sharedComponents.MuiButton,
			styleOverrides: {
				outlined: {
					backgroundColor: white,
				},
			},
		},
		MuiTableRow: {
			styleOverrides: {
				root: {
					"&:nth-of-type(even)": {
						backgroundColor: "rgba(214,219,230,0.25)",
					},
				},
			},
		},
		MuiTableCell: {
			styleOverrides: {
				root: {
					borderBottomColor: goniniBlue[50],
				},
				head: {
					backgroundColor: "#eceff4",
					color: goniniBlue[900],
					fontWeight: 600,
				},
			},
		},
	},
});

export const DarkTheme = createTheme({
	palette: {
		mode: "dark",
		primary: {
			main: goniniBlue[300],
			light: goniniBlue[200],
			dark: goniniBlue[500],
			contrastText: white,
		},
		secondary: {
			main: goniniBlue[200],
			light: goniniBlue[100],
			dark: goniniBlue[400],
			contrastText: goniniBlue[950],
		},
		tertiary: {
			main: white,
		},
		destructive: {
			main: "rgba(214,96,96,1)",
			contrastText: white,
		},
		success: {
			main: goniniGreen[400],
			light: goniniGreen[300],
			dark: goniniGreen[600],
			contrastText: goniniBlue[950],
		},
		warning: {
			main: goniniOrange[400],
			light: goniniOrange[300],
			dark: goniniOrange[600],
			contrastText: goniniBlue[950],
		},
		info: {
			main: goniniBlue[200],
			contrastText: goniniBlue[950],
		},
		text: {
			primary: white,
			secondary: goniniBlue[200],
		},
		divider: goniniBlue[800],
		appbar: {
			main: goniniBlue[950],
			contrastText: white,
		},
		statusbox: {
			main: goniniBlue[900],
		},
		background: {
			default: goniniBlue[950],
			paper: goniniBlue[900],
		},
		alert: {
			main: goniniBlue[900],
		},
	},
	typography,
	shape,
	components: {
		...sharedComponents,
		MuiButton: {
			...sharedComponents.MuiButton,
			styleOverrides: {
				outlined: {
					backgroundColor: "transparent",
				},
			},
		},
		MuiTableRow: {
			styleOverrides: {
				root: {
					"&:nth-of-type(even)": {
						backgroundColor: "rgba(25,38,66,0.45)",
					},
				},
			},
		},
		MuiTableCell: {
			styleOverrides: {
				root: {
					borderBottomColor: goniniBlue[800],
				},
				head: {
					backgroundColor: goniniBlue[800],
					color: white,
					fontWeight: 600,
				},
			},
		},
	},
});
